import React, { useMemo, useState } from "react";

const SectionHeader = ({ styles, title, desc }) => (
  <div className={styles.sectionHeader}>
    <h2 className={styles.h2}>{title}</h2>
    <div className={styles.divider} />
    {desc ? <p className={styles.sectionDesc}>{desc}</p> : null}
  </div>
);

const ToggleBar = ({ styles, active, onToggle, label }) => (
  <button
    type="button"
    className={`${styles.toggleBtn} ${active ? styles.toggleBtnActive : ""}`}
    onClick={onToggle}
  >
    <span>{label}</span>
    <span className={styles.toggleIcon}>{active ? "−" : "+"}</span>
  </button>
);

const Hero = ({ styles, onToggleFeatures, onToggleBenefits, openFeatures, openBenefits }) => (
  <section className={styles.section}>
    <div className={styles.container}>
      <div className={styles.heroCenter}>
        <h1 className={styles.h1}>Documan</h1>

        <p className={styles.heroLead}>
          <b>조직의 모든 지식을 하나의 AI 시스템으로 연결하다</b>
        </p>

        <p className={styles.heroText}>
          Documan은 기업·기관 내부의 방대한 문서를 AI로 학습시켜 누구나 자연어로 질문하고 정확한 답변을 얻을 수 있도록
          지원하는 <b>차세대 지식관리·문서 자동화 플랫폼</b>입니다. RAG 기술을 활용하여 신뢰성 있는 답변과 최신 정보 유지가 강점입니다.
        </p>

        <div className={styles.heroTags}>
          <span className={styles.tag}>RAG 기반</span>
          <span className={styles.tag}>권한 기반 보안</span>
          <span className={styles.tag}>실시간 업데이트</span>
          <span className={styles.tag}>자동 보고서</span>
        </div>

        {/* ✅ 토글 버튼 */}
        <div className={styles.heroCtaRow}>
          <ToggleBar
            styles={styles}
            label="핵심 기능 보기"
            active={openFeatures}
            onToggle={onToggleFeatures}
          />
          <ToggleBar
            styles={styles}
            label="도입 효과 보기"
            active={openBenefits}
            onToggle={onToggleBenefits}
          />
        </div>
      </div>
    </div>
  </section>
);

const RowCard = ({ styles, title, desc, bullets, variant = "feature" }) => (
  <div className={`${styles.rowCard} ${variant === "benefit" ? styles.rowCardBenefit : ""}`}>
    <div className={styles.rowCardHeader}>
      <div className={styles.rowCardTitle}>{title}</div>
      <div className={styles.rowCardDesc}>{desc}</div>
    </div>

    {bullets?.length ? (
      <ul className={styles.cleanList}>
        {bullets.map((b, i) => (
          <li key={i} className={styles.cleanListItem}>
            <span className={styles.bulletText}>{b}</span>
          </li>
        ))}
      </ul>
    ) : null}
  </div>
);

const FeaturesPanel = ({ styles }) => {
  const data = useMemo(
    () => [
      {
        title: "조직 내부 문서 자동 학습 (RAG 기반 지식화)",
        desc: "규정, 지침, 업무 문서, 보고서, 회의자료 등을 자동으로 수집·분석하여 의미 단위로 정교하게 학습합니다.",
        bullets: [
          "PDF, Word, HWP, 이미지(OCR) 등 다양한 문서 지원",
          "문장·단락·섹션 단위 자동 Chunking",
          "벡터DB를 통한 고급 의미 검색",
          "기업 데이터 기반 Private AI 구축",
        ],
      },
      {
        title: "권한 기반 AI 지식 질의응답",
        desc: "자연어로 질문하면 권한 범위 내 문서만 활용하여 정확한 답변을 제공합니다. 부서/직급별 접근 권한을 자동 적용합니다.",
        bullets: [
          "규정/정책 질의응답",
          "업무 매뉴얼 검색",
          "민감 문서 접근 통제",
          "관리자/사용자 역할 분리",
        ],
      },
      {
        title: "프로젝트 단위 문서 공유 및 실시간 업데이트",
        desc: "학습된 문서를 프로젝트 단위로 관리·공유하고, 문서 변경 사항을 실시간으로 AI 지식에 반영합니다.",
        bullets: [
          "문서 업데이트 즉시 AI 지식 반영",
          "팀·프로젝트 기반 협업 최적화",
          "다국적 조직 또는 여러 부서 운영 환경에 적합",
        ],
      },
      {
        title: "AI 기반 자동 보고서 생성",
        desc: "학습된 정보를 기반으로 기획서, 분석 보고서, 정책 요약, 검토 의견 등 다양한 문서를 AI가 자동 생성합니다.",
        bullets: [
          "규정/매뉴얼 요약 보고서",
          "정책 비교·변경 분석",
          "감독기관 보고자료 자동 생성",
          "프로젝트 회의록 요약",
        ],
      },
      {
        title: "정확성 중심의 AI — 할루시네이션 최소화",
        desc: "학습된 문서 범위 내에서만 근거를 찾아 응답하도록 설계해 진실성·정확성·신뢰성을 확보했습니다.",
        bullets: [
          "문서 출처 기반 답변(근거 표시 가능)",
          "학습 범위를 벗어난 질문은 “정보 없음” 처리",
          "AI 답변의 일관성·정확성 극대화",
        ],
      },
    ],
    []
  );

  return (
    <section className={styles.section} id="documan-features">
      <div className={styles.container}>
        <SectionHeader
          styles={styles}
          title="핵심 기능"
          desc="문서 수집·지식화부터 권한 기반 질의응답, 실시간 업데이트, 자동 보고서까지 — 조직의 지식을 정확하게 연결합니다."
        />

        {/* “큰 패널 + 내부 카드” */}
        <div className={styles.panel}>
          <div className={styles.panelTopDots}>
            <span className={styles.panelDot} />
            <span className={styles.panelDot} />
            <span className={styles.panelDot} />
          </div>

          <div className={styles.panelBody}>
            {data.map((x, i) => (
              <RowCard 
                key={i} 
                styles={styles} 
                title={x.title} 
                desc={x.desc} 
                bullets={x.bullets} 
                variant="feature"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const BenefitsPanel = ({ styles }) => {
  const data = useMemo(
    () => [
      {
        title: "조직 지식 활용도 극대화",
        desc: "문서가 많아질수록 정보 탐색이 어려워집니다. Documan은 조직 내 지식 접근성을 획기적으로 개선합니다.",
      },
      {
        title: "직원 생산성 향상",
        desc: "문서 검색·정리·보고서 작성 시간이 크게 단축되어 더 가치 있는 의사결정 및 전략 업무에 집중할 수 있습니다.",
      },
      {
        title: "정보의 신뢰성 확보",
        desc: "학습된 문서 내에서만 답변하므로 컴플라이언스/내부규정/보안 관리가 필요한 기관에서도 안심하고 사용할 수 있습니다.",
      },
      {
        title: "실시간 업데이트로 최신성 유지",
        desc: "규정, 절차, 정책 변경 사항이 즉시 AI에 반영되어 항상 최신 정보 기반 업무가 가능합니다.",
      },
    ],
    []
  );

  return (
    <section className={styles.section} id="documan-benefits">
      <div className={styles.container}>
        <SectionHeader
          styles={styles}
          title="도입 효과"
          desc="지식 접근성, 생산성, 신뢰성, 최신성까지 — 문서 중심 조직의 병목을 해소합니다."
        />

        <div className={styles.panel}>
          <div className={styles.panelTopDots}>
            <span className={styles.panelDot} />
            <span className={styles.panelDot} />
            <span className={styles.panelDot} />
          </div>

          <div className={`${styles.panelBody} ${styles.panelBodyGrid2}`}>
            {data.map((x, i) => (
              <RowCard 
                key={i} 
                styles={styles} 
                title={x.title} 
                desc={x.desc} 
                variant="benefit"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TargetOrgs = ({ styles }) => {
  const items = [
    "규정·정책 문서가 많은 공공기관/지자체",
    "매뉴얼·지침이 복잡한 제조·의료 분야",
    "고객지원 문의가 많은 기업(CS 자동화)",
    "문서 및 지식 공유가 어려운 대규모 조직",
    "보안 요구가 높은 군, 금융, 공기업",
    "교육기관 및 연구기관의 문서 검색 자동화",
  ];

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <SectionHeader styles={styles} title="Documan이 필요한 조직" />
        <div className={styles.targetWrap}>
          {items.map((t, i) => (
            <div className={styles.targetItem} key={i}>
              {t}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Slogan = ({ styles }) => (
  <section className={styles.section}>
    <div className={styles.container}>
      <div className={styles.sloganCard}>
        <div className={styles.sloganLabel}>제품 슬로건</div>
        <div className={styles.sloganText}>“문서를 이해하는 AI, Documan”</div>
      </div>
    </div>
  </section>
);

const DocumanSection = ({ styles }) => {
  const [openFeatures, setOpenFeatures] = useState(false);
  const [openBenefits, setOpenBenefits] = useState(false);

  const toggleFeatures = () => setOpenFeatures((v) => !v);
  const toggleBenefits = () => setOpenBenefits((v) => !v);

  return (
    <>
      <Hero
        styles={styles}
        openFeatures={openFeatures}
        openBenefits={openBenefits}
        onToggleFeatures={toggleFeatures}
        onToggleBenefits={toggleBenefits}
      />

      {/* ✅ 버튼 누르기 전엔 안 보임 / 다시 누르면 접힘 */}
      {openFeatures ? <FeaturesPanel styles={styles} /> : null}
      {openBenefits ? <BenefitsPanel styles={styles} /> : null}

      <TargetOrgs styles={styles} />
      <Slogan styles={styles} />
    </>
  );
};

export default DocumanSection;
